import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { gruppeApi, type GruppeCreateData } from '../api/gruppe';
import SortTh from '../components/SortTh';
import Spinner from '../components/Spinner';
import EmptyTableRow from '../components/EmptyTableRow';
import Pagination from '../components/Pagination';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useCan } from '../hooks/useCan';

export default function GruppenList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useSessionFilter<string>('gruppen_searchText', '');
  const [sortBy, setSortBy] = useSessionFilter<string>('gruppen_sortBy', 'name');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('gruppen_sortDir', 'asc');
  const [showHidden, setShowHidden] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState<GruppeCreateData>({ name: '', beschreibung: '' });
  const [createError, setCreateError] = useState('');
  const canRestoreHidden = useCan('restore_hidden');
  const canRestoreOwn = useCan('restore_own');
  const queryClient = useQueryClient();
  const perPage = 50;

  const queryArgs = { page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir, include_hidden: showHidden };
  const { data, isLoading } = useQuery({
    queryKey: ['gruppen', queryArgs],
    queryFn: () => gruppeApi.getGruppen(queryArgs),
  });

  const restoreMutation = useMutation({
    mutationFn: (id: number) => gruppeApi.restoreGruppe(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['gruppen'] }),
  });

  const createMutation = useMutation({
    mutationFn: (body: GruppeCreateData) => gruppeApi.createGruppe(body),
    onSuccess: (gruppe) => {
      queryClient.invalidateQueries({ queryKey: ['gruppen'] });
      navigate(`/gruppen/${gruppe.id}`);
    },
    onError: () => {
      setCreateError('Fehler beim Erstellen der Gruppe.');
    },
  });

  const handleCreate = () => {
    if (!form.name.trim()) {
      setCreateError('Name ist erforderlich.');
      return;
    }
    setCreateError('');
    createMutation.mutate({
      name: form.name.trim(),
      beschreibung: form.beschreibung?.trim() || undefined,
    });
  };

  const inputClass = "w-full border rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
          <div className="flex gap-3">
            <input
              type="text"
              placeholder="Suche nach Name oder Beschreibung..."
              className="flex-1 border rounded-lg px-3 py-2 text-sm"
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            />
            <button
              onClick={() => { setShowCreateForm(true); setCreateError(''); }}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
            >
              + Neue Gruppe
            </button>
          </div>
          <p className="text-xs text-gray-500">
            Tipp: Inserate lassen sich auch direkt auf der Karte auswählen und zu einer Gruppe hinzufügen.
          </p>
          {(canRestoreHidden || canRestoreOwn) && (
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => { setShowHidden(e.target.checked); setPage(1); }}
              />
              Ausgeblendete anzeigen
            </label>
          )}
        </div>

        {showCreateForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h3 className="text-base font-medium text-gray-800 mb-4">Neue Gruppe anlegen</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="z.B. Altstadt, Kampagne Frühjahr, Uferpromenade"
                  className={inputClass}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Beschreibung</label>
                <textarea
                  value={form.beschreibung ?? ''}
                  onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
                  placeholder="Optionaler Kurzbeschrieb"
                  rows={2}
                  className={inputClass}
                />
              </div>
            </div>
            {createError && <p className="text-sm text-red-600 mt-2">{createError}</p>}
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleCreate}
                disabled={createMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {createMutation.isPending ? 'Wird angelegt...' : 'Anlegen'}
              </button>
              <button
                onClick={() => { setShowCreateForm(false); setForm({ name: '', beschreibung: '' }); setCreateError(''); }}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
              >
                Abbrechen
              </button>
            </div>
          </div>
        )}

        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortTh field="name" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Name</SortTh>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Beschreibung</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inserate</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Zuständig</th>
                    <SortTh field="created_at" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Erstellt</SortTh>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items.map((g) => (
                    <tr
                      key={g.id}
                      className={`hover:bg-gray-50 cursor-pointer ${g.deleted_at ? 'bg-slate-100 opacity-60' : ''}`}
                      onClick={() => navigate(`/gruppen/${g.id}`)}
                    >
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">{g.name}</td>
                      <td className="px-3 py-3 text-sm text-gray-500 max-w-xs truncate">{g.beschreibung || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-500">{g.inserate_count}</td>
                      <td className="px-3 py-3 text-sm text-gray-500">{g.assignment?.user?.full_name || '-'}</td>
                      <td className="px-3 py-3 text-sm text-gray-500 whitespace-nowrap">
                        {g.deleted_at ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); restoreMutation.mutate(g.id); }}
                            className="text-green-700 hover:text-green-900 text-sm font-medium"
                          >
                            Wiederherstellen
                          </button>
                        ) : (
                          new Date(g.created_at).toLocaleDateString('de-DE')
                        )}
                      </td>
                    </tr>
                  ))}
                  {data?.items.length === 0 && (
                    <EmptyTableRow colSpan={5} message="Keine Gruppen gefunden." />
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={data?.total ?? 0}
              entityLabel="Gruppen"
              onPageChange={setPage}
            />
          </>
        )}
      </main>
    </div>
  );
}
